import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Map, { Marker } from 'react-map-gl';
import { doFetch, doDelete } from '../../../redux/api/actions';
import { GET_TOUR_TRACKS, DELETE_AUDIO_TRACK } from '../../../redux/api/constants';
import AddTrackModal from './AddTrackModal';
import { useTranslation } from 'react-i18next';
import './AudioTrackManager.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoidG9yZ3lkIiwiYSI6ImNtNXN6ZGQ0bjBhbGIya3M5dHpwY2p3aDkifQ.placeholder';

function AudioTrackManager({ tourId }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const accessToken = useSelector(state => state.Auth.accessToken);
    const tracks = useSelector(state => state.API.tourTracks || []);

    const [showAddModal, setShowAddModal] = useState(false);
    const [editingTrack, setEditingTrack] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const miniMapRef = useRef();

    useEffect(() => {
        if (tourId) {
            dispatch(doFetch(GET_TOUR_TRACKS, `tourguide/tours/${tourId}/tracks`, accessToken));
        }
    }, [tourId, accessToken, dispatch]);

    const handleDelete = (trackId) => {
        dispatch(doDelete(DELETE_AUDIO_TRACK, `tourguide/tours/${tourId}/tracks/${trackId}`, accessToken));
        setDeleteConfirm(null);
        setTimeout(() => {
            dispatch(doFetch(GET_TOUR_TRACKS, `tourguide/tours/${tourId}/tracks`, accessToken));
        }, 500);
    };

    const handleAddSuccess = () => {
        setShowAddModal(false);
        setEditingTrack(null);
        dispatch(doFetch(GET_TOUR_TRACKS, `tourguide/tours/${tourId}/tracks`, accessToken));
    };

    const formatDuration = (seconds) => {
        if (!seconds) return null;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const sortedTracks = useMemo(
        () => [...tracks].sort((a, b) => a.sequence_order - b.sequence_order),
        [tracks]
    );

    // Compute center of all stops for the mini-map
    const mapCenter = sortedTracks.length > 0
        ? {
            lat: sortedTracks.reduce((s, t) => s + t.latitude, 0) / sortedTracks.length,
            lng: sortedTracks.reduce((s, t) => s + t.longitude, 0) / sortedTracks.length,
          }
        : null;

    // Fit mini-map to all stops when tracks load
    useEffect(() => {
        if (!miniMapRef.current || sortedTracks.length < 2) return;
        const lats = sortedTracks.map(t => t.latitude);
        const lngs = sortedTracks.map(t => t.longitude);
        miniMapRef.current.fitBounds(
            [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
            { padding: 40, duration: 0, maxZoom: 15 }
        );
    }, [sortedTracks]);

    const handleMiniMapClick = () => {
        if (!mapCenter) return;
        navigate(`/?lat=${mapCenter.lat}&lng=${mapCenter.lng}&zoom=13`);
    };

    const handleStopClick = (track) => {
        navigate(`/?lat=${track.latitude}&lng=${track.longitude}&zoom=16`);
    };

    return (
        <div className="audio-track-manager">
            <div className="section-header">
                <h3>{t('track-tour-stops')}</h3>
                <button className="btn-primary" onClick={() => setShowAddModal(true)}>
                    + {t('track-add-stop')}
                </button>
            </div>

            {/* Mini-map preview */}
            {sortedTracks.length > 0 && mapCenter && (
                <div className="mini-map-container" onClick={handleMiniMapClick} title={t('track-view-on-map')}>
                    <Map
                        ref={miniMapRef}
                        initialViewState={{
                            longitude: mapCenter.lng,
                            latitude: mapCenter.lat,
                            zoom: sortedTracks.length === 1 ? 14 : 11,
                        }}
                        mapStyle="mapbox://styles/mapbox/streets-v12"
                        mapboxAccessToken={MAPBOX_TOKEN}
                        interactive={false}
                        style={{ width: '100%', height: '100%' }}
                    >
                        {sortedTracks.map((track, index) => (
                            <Marker
                                key={track.id}
                                longitude={track.longitude}
                                latitude={track.latitude}
                                anchor="bottom"
                            >
                                <div className="mini-map-pin">{index + 1}</div>
                            </Marker>
                        ))}
                    </Map>
                    <div className="mini-map-overlay">
                        <span className="mini-map-label">{t('map-view-on-map-arrow')}</span>
                    </div>
                </div>
            )}

            {sortedTracks.length === 0 ? (
                <div className="empty-state">
                    <p>{t('track-empty')}</p>
                </div>
            ) : (
                <div className="tracks-list">
                    {sortedTracks.map((track, index) => (
                        <div key={track.id} className="track-card">
                            <div className="track-info">
                                <div className="track-sequence">{index + 1}</div>
                                <div className="track-details">
                                    <h4
                                        className="stop-title-link"
                                        onClick={() => handleStopClick(track)}
                                        title={t('track-view-on-map')}
                                    >
                                        {track.title}
                                    </h4>
                                    {track.description && (
                                        <p className="track-description">{track.description}</p>
                                    )}
                                    <div className="track-meta">
                                        {formatDuration(track.duration_seconds) && (
                                            <span className="duration">
                                                {formatDuration(track.duration_seconds)}
                                            </span>
                                        )}
                                        {track.text_content && (
                                            <span className="has-text">{t('text')}</span>
                                        )}
                                        <span className="location">
                                            {track.latitude.toFixed(4)}, {track.longitude.toFixed(4)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="track-actions">
                                <button
                                    className="btn-outline-small"
                                    onClick={() => {
                                        setEditingTrack(track);
                                        setShowAddModal(true);
                                    }}
                                >
                                    {t('edit')}
                                </button>
                                <button
                                    className="btn-danger-small"
                                    onClick={() => setDeleteConfirm(track.id)}
                                >
                                    {t('delete')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showAddModal && (
                <AddTrackModal
                    tourId={tourId}
                    track={editingTrack}
                    onClose={() => {
                        setShowAddModal(false);
                        setEditingTrack(null);
                    }}
                    onSuccess={handleAddSuccess}
                />
            )}

            {deleteConfirm && (
                <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{t('track-delete-title')}</h3>
                        <p>{t('track-delete-message')}</p>
                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>
                                {t('cancel')}
                            </button>
                            <button className="btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                                {t('track-delete-stop')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AudioTrackManager;
